/**
 * Audit Log API Service
 *
 * Provides API integration for workforce audit logs
 * Implements tenant-aware API calls following Amplify patterns
 *
 * Related Story: WORKFORCE-V2-story-2.004
 * Backend Endpoint: GET /workforce/audit-logs
 */

import { get } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from './api-utils'

const API_NAME = 'workforce'

/**
 * Audit log entry structure returned from API
 */
export interface AuditLogEntry {
  auditId: string
  timestamp: string
  userId: string
  userName: string
  entityType: 'EMPLOYEE' | 'DEPARTMENT' | 'ROLE' | 'PAYROLL'
  entityId: string
  operation: string
  beforeValue?: string // JSON string
  afterValue?: string // JSON string
  ipAddress?: string
  userAgent?: string
}

/**
 * Query parameters for fetching audit logs
 */
export interface AuditLogQueryParams {
  entityType?: 'EMPLOYEE' | 'DEPARTMENT' | 'ROLE' | 'PAYROLL'
  userId?: string
  startDate?: string // ISO 8601
  endDate?: string // ISO 8601
  limit?: number
  nextToken?: string
}

/**
 * Paginated response from audit logs API
 */
export interface AuditLogResponse {
  items: AuditLogEntry[]
  pagination: {
    nextToken?: string
    count: number
    limit: number
    hasNextPage: boolean
  }
}

/**
 * Fetch audit logs with optional filters and pagination
 *
 * @param params - Query parameters (filters, pagination)
 * @returns Paginated list of audit log entries
 */
export const fetchAuditLogs = async (
  params?: AuditLogQueryParams
): Promise<AuditLogResponse> => {
  // Build query string
  const queryParams = new URLSearchParams()

  if (params?.entityType) {
    queryParams.append('entityType', params.entityType)
  }

  if (params?.userId) {
    queryParams.append('userId', params.userId)
  }

  if (params?.startDate) {
    queryParams.append('startDate', params.startDate)
  }

  if (params?.endDate) {
    queryParams.append('endDate', params.endDate)
  }

  if (params?.limit) {
    queryParams.append('limit', params.limit.toString())
  }

  if (params?.nextToken) {
    queryParams.append('nextToken', params.nextToken)
  }

  // Make API request
  const restOperation = get({
    apiName: API_NAME,
    path: `/workforce/audit-logs?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  // Parse response
  const response = await restOperation.response
  const data = await response.body.json() as unknown as { items: AuditLogEntry[] }

  // Extract pagination metadata from headers
  const nextToken = response.headers['x-next-token']
  const itemCount = response.headers['x-item-count']

  return {
    items: data.items || [],
    pagination: {
      count: itemCount ? parseInt(itemCount) : data.items?.length || 0,
      limit: params?.limit || 100,
      hasNextPage: !!nextToken,
      nextToken: nextToken || undefined
    }
  }
}

/**
 * Export audit logs to CSV format
 *
 * @param logs - Audit log entries to export
 * @returns CSV file download
 */
export const exportAuditLogsToCsv = (logs: AuditLogEntry[]): void => {
  // CSV headers
  const headers = [
    'Timestamp',
    'User',
    'Entity Type',
    'Operation',
    'Entity ID',
    'Before Value',
    'After Value',
    'IP Address',
    'User Agent'
  ]

  // Convert logs to CSV rows
  const rows = logs.map(log => [
    log.timestamp,
    log.userName,
    log.entityType,
    log.operation,
    log.entityId,
    log.beforeValue || '',
    log.afterValue || '',
    log.ipAddress || '',
    log.userAgent || ''
  ])

  // Build CSV content
  const csvContent = [
    headers.join(','),
    ...rows.map(row =>
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    )
  ].join('\n')

  // Create blob and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url

  // Format filename with current date
  const dateStr = new Date().toISOString().split('T')[0]
  link.download = `audit-logs-export-${dateStr}.csv`

  // Trigger download
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
